import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CmsProject, CmsAmenityItem, CmsSpecificationGroup, CmsSpecificationItem } from '../../types/cms';

const GOLD = '#9d6f32';
const BORDER = '#e8e8e8';

const AVAILABLE_ICONS = [
  { icon: 'Pool', label: 'Infinity Swimming Pool' },
  { icon: 'Barbell', label: 'Gymnasium & Fitness' },
  { icon: 'Buildings', label: 'Grand Clubhouse' },
  { icon: 'PlayCircle', label: "Children's Play Zone" },
  { icon: 'Tree', label: 'Landscaped Garden & Gazebo' },
  { icon: 'Leaf', label: 'Yoga & Meditation Deck' },
  { icon: 'ShieldCheck', label: '24/7 CCTV & Security' },
  { icon: 'Car', label: 'Podium & Basement Parking' },
  { icon: 'Sun', label: 'Solar Powered Common Areas' },
  { icon: 'Lightning', label: 'EV Vehicle Fast Charging' },
  { icon: 'Briefcase', label: 'Executive Business Lounge' },
  { icon: 'Football', label: 'Multipurpose Sports Court' },
];

interface ProjectAmenitiesTabProps {
  project: CmsProject;
  onChange: (updated: CmsProject) => void;
}

export const ProjectAmenitiesTab: React.FC<ProjectAmenitiesTabProps> = ({ project, onChange }) => {
  // 1. Highlights
  const handleAddHighlight = () => {
    onChange({
      ...project,
      highlights: [...project.highlights, 'New key project highlight'],
    });
  };

  const handleHighlightChange = (index: number, val: string) => {
    const next = [...project.highlights];
    next[index] = val;
    onChange({ ...project, highlights: next });
  };

  const handleRemoveHighlight = (index: number) => {
    onChange({
      ...project,
      highlights: project.highlights.filter((_, i) => i !== index),
    });
  };

  // 2. Amenities
  const handleToggleAmenity = (iconItem: { icon: string; label: string }) => {
    const exists = project.amenities.some((a) => a.icon === iconItem.icon);
    if (exists) {
      onChange({
        ...project,
        amenities: project.amenities.filter((a) => a.icon !== iconItem.icon),
      });
    } else {
      const newAmenity: CmsAmenityItem = {
        id: `a_${Date.now()}`,
        icon: iconItem.icon,
        label: iconItem.label,
        category: 'wellness',
      };
      onChange({
        ...project,
        amenities: [...project.amenities, newAmenity],
      });
    }
  };

  // 3. Specifications
  const handleAddSpecGroup = () => {
    const newGroup: CmsSpecificationGroup = {
      id: `s_${Date.now()}`,
      heading: 'New Specification Heading',
      items: [{ label: 'Item Label', value: 'Item Value Description' }],
    };
    onChange({
      ...project,
      specifications: [...project.specifications, newGroup],
    });
  };

  const handleRemoveSpecGroup = (groupId: string) => {
    onChange({
      ...project,
      specifications: project.specifications.filter((g) => g.id !== groupId),
    });
  };

  const handleSpecHeadingChange = (groupId: string, heading: string) => {
    onChange({
      ...project,
      specifications: project.specifications.map((g) => (g.id === groupId ? { ...g, heading } : g)),
    });
  };

  const handleAddSpecItem = (groupId: string) => {
    onChange({
      ...project,
      specifications: project.specifications.map((g) =>
        g.id === groupId
          ? { ...g, items: [...g.items, { label: 'New Feature', value: 'Details' }] }
          : g
      ),
    });
  };

  const handleSpecItemChange = (groupId: string, itemIdx: number, field: keyof CmsSpecificationItem, val: string) => {
    onChange({
      ...project,
      specifications: project.specifications.map((g) => {
        if (g.id !== groupId) return g;
        const nextItems = [...g.items];
        nextItems[itemIdx] = { ...nextItems[itemIdx], [field]: val };
        return { ...g, items: nextItems };
      }),
    });
  };

  const handleRemoveSpecItem = (groupId: string, itemIdx: number) => {
    onChange({
      ...project,
      specifications: project.specifications.map((g) => {
        if (g.id !== groupId) return g;
        return { ...g, items: g.items.filter((_, i) => i !== itemIdx) };
      }),
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
      {/* 1. Project Highlights */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              ✨ Key Project Highlights & USPs
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
              These bullet points are featured prominently at the top of the project page.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddHighlight}
            sx={{
              borderColor: GOLD,
              color: GOLD,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(157,111,50,0.06)' },
            }}
          >
            Add Highlight
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {project.highlights.map((hl, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircleOutlineIcon sx={{ color: GOLD, fontSize: 20 }} />
              <TextField
                fullWidth
                size="small"
                value={hl}
                onChange={(e) => handleHighlightChange(idx, e.target.value)}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveHighlight(idx)}
                disabled={project.highlights.length <= 1}
                sx={{ color: '#ef4444' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* 2. Visual Amenity Selector */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
          🏊 Lifestyle Amenities & Facilities
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b6b6b', mb: 2.5 }}>
          Click an amenity to toggle active status on the public website.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
          {AVAILABLE_ICONS.map((iconItem) => {
            const isSelected = project.amenities.some((a) => a.icon === iconItem.icon);
            return (
              <Box
                key={iconItem.icon}
                onClick={() => handleToggleAmenity(iconItem)}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: `1.5px solid ${isSelected ? GOLD : BORDER}`,
                  bgcolor: isSelected ? 'rgba(157,111,50,0.06)' : '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: GOLD,
                  },
                }}
              >
                <Chip
                  label={isSelected ? 'Active' : 'Add'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 10,
                    bgcolor: isSelected ? GOLD : '#f3f4f6',
                    color: isSelected ? '#fff' : '#6b6b6b',
                  }}
                />
                <Typography sx={{ fontWeight: isSelected ? 700 : 500, fontSize: 13, color: isSelected ? '#1a1a1a' : '#4b5563' }}>
                  {iconItem.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* 3. Construction Specifications */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              🏗️ Technical Construction Specifications
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
              Materials, brands, and architectural specifications (Structure, Flooring, Sanitary, Electrical).
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddSpecGroup}
            sx={{
              borderColor: GOLD,
              color: GOLD,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(157,111,50,0.06)' },
            }}
          >
            Add Specification Section
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {project.specifications.map((group) => (
            <Paper key={group.id} sx={{ p: 2.5, bgcolor: '#fafafa', border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  label="Category Heading"
                  value={group.heading}
                  onChange={(e) => handleSpecHeadingChange(group.id, e.target.value)}
                  sx={{ minWidth: 260 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddSpecItem(group.id)}
                    sx={{ textTransform: 'none', color: GOLD, fontWeight: 600 }}
                  >
                    Add Item
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSpecGroup(group.id)}
                    sx={{ color: '#ef4444' }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {group.items.map((item, itemIdx) => (
                  <Box
                    key={itemIdx}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 2fr auto' },
                      gap: 1.5,
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      size="small"
                      label="Feature / Item"
                      value={item.label}
                      onChange={(e) => handleSpecItemChange(group.id, itemIdx, 'label', e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Specification / Brand"
                      value={item.value}
                      onChange={(e) => handleSpecItemChange(group.id, itemIdx, 'value', e.target.value)}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSpecItem(group.id, itemIdx)}
                      disabled={group.items.length <= 1}
                      sx={{ color: '#ef4444' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};
